import { z } from 'zod';
// Agent Configuration Schema - matches the exact structure from agent.py
export const SimpleDemoConfigSchema = z.object({
    id: z.string(),
    title: z.string(),
    description: z.string(),
    industry: z.string(),
    businessDomain: z.string(),
    orchestrationMode: z.boolean().optional(),
    agentDiscoveryDelay: z.number().optional(),
    interactionDelays: z.object({
        orchestratorThinking: z.number(),
        agentProcessing: z.number(),
        responseCompilation: z.number(),
    }).optional(),
    scenario: z.object({
        title: z.string(),
        description: z.string(),
        icon: z.string(),
    }),
    uiConfig: z.object({
        theme: z.string(),
        headerGradient: z.string(),
        primaryColor: z.string(),
        headerIcon: z.string().optional(),
    }),
    backendConfig: z.object({
        defaultBackend: z.string(),
        localFallback: z.boolean(),
    }).optional(),
    createdAt: z.string(),
});
// Orchestrator Agent Schema
export const OrchestratorAgentSchema = z.object({
    id: z.literal("orchestrator"),
    name: z.string(),
    avatar: z.string(),
    role: z.string(),
    capabilities: z.array(z.string()),
    initializationSteps: z.array(z.string()),
});
export const SimpleAgentConfigSchema = z.array(z.object({
    id: z.string(),
    name: z.string(),
    avatar: z.string(),
    role: z.string(),
    expertise: z.array(z.string()),
    dataSources: z.array(z.string()),
    tools: z.array(z.string()),
    actions: z.array(z.string()),
    personality: z.string(),
    orchestrationRole: z.string().optional(),
    callPriority: z.number().optional(),
    dependencies: z.array(z.string()).optional(),
}));
// Orchestration Flow Schema
export const OrchestrationFlowSchema = z.object({
    orchestratorAnalysis: z.string(),
    agentCallOrder: z.array(z.string()),
    agentActions: z.record(z.string(), z.object({
        action: z.string(),
        tools: z.array(z.string()),
        reasoning: z.string(),
    })),
    synthesisProcess: z.string(),
});
export const SimpleExamplesConfigSchema = z.array(z.object({
    category: z.string(),
    query: z.string(),
    expectedAgents: z.array(z.string()),
    description: z.string().optional(),
    mockResponse: z.string().optional(),
    orchestrationFlow: OrchestrationFlowSchema.optional(),
}));
// Interaction Step Schema
export const InteractionStepSchema = z.object({
    stepId: z.string(),
    agent: z.string(),
    action: z.enum(["analyze", "call_agent", "use_tool", "synthesize"]),
    description: z.string(),
    duration: z.number(),
    dependencies: z.array(z.string()),
});
export const InteractionFlowsSchema = z.record(z.string(), z.array(InteractionStepSchema));
// Configuration object schema - the inner structure within the agent response
export const ConfigurationSchema = z.object({
    config: SimpleDemoConfigSchema,
    orchestrator: OrchestratorAgentSchema.optional(),
    agents: SimpleAgentConfigSchema,
    examples: SimpleExamplesConfigSchema,
    interactionFlows: InteractionFlowsSchema.optional(),
    welcomeMessage: z.string().optional(),
    responseTemplates: z.object({
        default: z.string().optional(),
    }).catchall(z.string()).optional(),
    metadata: z.object({
        generated_at: z.string(),
        generation_id: z.string(),
        use_case_input: z.string(),
        model_used: z.string(),
        context: z.record(z.string(), z.any()),
    }).optional(),
});
// Full Agent Response Schema - matches the exact structure returned by agent.py
export const AgentResponseSchema = z.object({
    success: z.boolean(),
    use_case_processed: z.string(),
    configuration: ConfigurationSchema,
    deployment_ready: z.boolean(),
    processing_timestamp: z.string(),
});
// Error Response Schema - for when agent returns an error
export const AgentErrorResponseSchema = z.object({
    success: z.literal(false),
    error: z.string(),
    details: z.string().optional(),
    use_case_attempted: z.string().optional(),
    timestamp: z.string(),
});
// Union type for all possible agent responses
export const AgentFullResponseSchema = z.union([
    AgentResponseSchema,
    AgentErrorResponseSchema,
]);
//# sourceMappingURL=demo-config.js.map